import { useCallback, useEffect, useState } from 'react'

import logger from '@/utils/logger'

const log = logger({ category: 'useUpdateAvailable' })

/** Written by the emit-version-file plugin; see vite.config.mts. */
interface DeployedVersion {
  package?: string
  git?: string
}

/** Long, because the focus check is what actually catches a parked tab. */
const POLL_INTERVAL_MS = 20 * 60 * 1000

/**
 * Whether a newer client build has been deployed since this one loaded.
 *
 * The client and the API deploy separately, so the API cannot tell a stale client that a
 * newer client exists — its version is unchanged. The signal comes instead from
 * /version.json, which ships with the client deploy and carries the same fingerprint that
 * vite bakes into __APP_VERSION__.
 *
 * Worth knowing: this app has no route-level code splitting, so a stale tab never breaks on
 * a missing chunk. It keeps running happily on the bundle it already has, with no symptom —
 * which is exactly why an explicit check is needed rather than relying on a load failure.
 *
 * Checked on tab focus and on a long interval. Reporting only; the caller decides whether to
 * prompt, because force-reloading would discard whatever the user was in the middle of.
 */
const useUpdateAvailable = (): boolean => {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  const check = useCallback(async () => {
    try {
      // Cache-busted both ways: the query param defeats an intermediate cache, no-store the
      // browser's own. Without this a stale client can keep reading its own cached copy.
      const response = await fetch(`/version.json?t=${String(Date.now())}`, { cache: 'no-store' })
      if (!response.ok) return
      const deployed = (await response.json()) as DeployedVersion
      const running = __APP_VERSION__.version.git
      if (deployed.git && running && deployed.git !== running) {
        log.info('newer client deployed: %s (running %s)', deployed.git, running)
        setUpdateAvailable(true)
      }
    } catch (ex) {
      // Offline, or a deploy swapping files underneath us. Try again on the next tick.
      log.debug('version check failed: %o', ex)
    }
  }, [])

  useEffect(() => {
    // No dev guard needed: vite only emits version.json on build, so in dev the fetch 404s
    // and the !ok path below makes this a no-op.
    let cancelled = false
    const run = () => {
      if (!cancelled && document.visibilityState === 'visible') void check()
    }

    run()
    const timer = setInterval(run, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', run)
    return () => {
      cancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', run)
    }
  }, [check])

  return updateAvailable
}

export default useUpdateAvailable
